// Copies a rendered DOM node to the clipboard as BOTH rich HTML and plain
// text (so paste into Gmail/Word keeps formatting, paste into plain inputs
// stays readable). Ported from legacy CopyButton/utils.ts `copyWithFormatting`,
// trimmed to the parts the operations report modals use. Elements marked with
// the `data-no-copy` attribute are stripped from the copy.

// Build a readable plain-text version by walking the cloned DOM: block-level
// elements get line breaks, inline elements stay on the same line.
const INLINE_TAGS = new Set([
  'span',
  'a',
  'strong',
  'em',
  'b',
  'i',
  'time',
  'small',
]);

const toPlainText = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  const inner = Array.from(el.childNodes).map(toPlainText).join('');
  if (tag === 'br') return '\n';
  return INLINE_TAGS.has(tag) ? inner : `${inner}\n`;
};

const collapseBlankLines = (text: string): string =>
  text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

// Convert an HTML *string* (e.g. tiptap rich-text private notes) into readable
// plain text — block elements become line breaks, inline elements stay inline,
// all tags are dropped. Use this before copying rich-text to the clipboard so
// the paste target gets clean text instead of raw `<p>`/`<span>` markup.
export const htmlToPlainText = (html: string | null | undefined): string => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return collapseBlankLines(toPlainText(doc.body));
};

export const copyWithFormatting = async (
  node: HTMLElement | null
): Promise<boolean> => {
  if (!node) return false;
  const clone = node.cloneNode(true) as HTMLElement;
  for (const el of clone.querySelectorAll('[data-no-copy]')) el.remove();
  // Surface link URLs in the copied output (anchors lose their href on paste).
  for (const link of clone.querySelectorAll('a')) {
    const href = link.getAttribute('href');
    if (href) link.after(` (${href})`);
  }

  const html = clone.outerHTML;
  const text = collapseBlankLines(toPlainText(clone));

  const clip = navigator.clipboard;
  try {
    if (
      clip &&
      typeof clip.write === 'function' &&
      typeof ClipboardItem !== 'undefined'
    ) {
      await clip.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return true;
    }
    await clip.writeText(text);
    return true;
  } catch {
    return false;
  }
};
